import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import { MediaService } from '../../../services/media/media.service';
import { ProfileImageService } from '../../../services/profile-image/profile-image.service';
import { NativeImagePickerService } from '../../../services/native-image-picker/native-image-picker.service';
import { resolveUploadMimeTypeFromFile } from '../../../utils/upload-mime';
import { PictureModalComponent } from '../../picture-modal/picture-modal.component';

export type ProfileImageEntityKind = 'user' | 'location';

@Component({
  selector: 'app-duna-profile-image',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    PictureModalComponent,
  ],
  templateUrl: './duna-profile-image.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-profile-image.component.scss',
})
export class DunaProfileImageComponent implements OnChanges, OnDestroy {
  @Input() obfuscatedName: string | null = null;
  @Input() fallbackInitials = '?';
  @Input() size = 64;
  @Input() canEdit = false;
  @Input() scopeType: 'user' | 'location' = 'user';
  @Input() scopeId?: number;
  @Input() entityUuid = '';
  @Input() entityKind: ProfileImageEntityKind = 'user';
  @Input() placeholderSrc: string | null = null;
  @Input() variant: 'circle' | 'card' = 'circle';
  @Input() enablePreview = true;

  @Output() obfuscatedNameChange = new EventEmitter<string>();
  @Output() uploadError = new EventEmitter<string>();

  @ViewChild(PictureModalComponent) pictureModal?: PictureModalComponent;

  imageSrc: string | null = null;
  isLoading = false;
  isUploading = false;
  uploadProgress = 0;
  errorMessage: string | null = null;

  private blobUrl: string | null = null;
  private loadToken = 0;

  constructor(
    private mediaService: MediaService,
    private profileImageService: ProfileImageService,
    private nativeImagePicker: NativeImagePickerService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['obfuscatedName']) {
      void this.loadImage();
    }
  }

  ngOnDestroy(): void {
    this.revokeBlobUrl();
  }

  get showPlaceholderImage(): boolean {
    return !this.imageSrc && !this.isLoading && !!this.placeholderSrc;
  }

  get fallbackInitialsDisplay(): boolean {
    return !this.imageSrc && !this.isLoading && !this.placeholderSrc;
  }

  get spinnerSize(): number {
    return Math.min(this.size * 0.5, 32);
  }

  get canPreviewImage(): boolean {
    return (
      this.enablePreview &&
      !this.isLoading &&
      !this.isUploading &&
      (!!this.imageSrc || this.showPlaceholderImage)
    );
  }

  get previewTooltip(): string {
    return this.canPreviewImage ? 'Klik for at se billede' : '';
  }

  onImageClick(event: Event): void {
    if (!this.canPreviewImage) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const src = this.imageSrc ?? this.placeholderSrc;
    if (src) {
      this.pictureModal?.open(src, this.fallbackInitials);
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      await this.uploadSelectedFile(file);
    }
  }

  async triggerFileInput(fileInput: HTMLInputElement): Promise<void> {
    if (!this.canEdit || this.isUploading) {
      return;
    }

    if (this.nativeImagePicker.usesNativeGalleryPicker()) {
      const file = await this.nativeImagePicker.pickSingleImageFile();
      if (file) {
        await this.uploadSelectedFile(file);
      }
      return;
    }

    fileInput.click();
  }

  private async uploadSelectedFile(file: File): Promise<void> {
    if (!this.canEdit || !this.entityUuid || this.scopeId == null) {
      return;
    }

    const mime = resolveUploadMimeTypeFromFile(file);
    if (!mime.startsWith('image/')) {
      const msg = 'Kun billedfiler er tilladt (JPEG, PNG, GIF, WebP m.fl.)';
      this.errorMessage = msg;
      this.uploadError.emit(msg);
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.errorMessage = null;
    this.cdr.markForCheck();

    try {
      const uploadedName = await this.mediaService.uploadFileToFolder(
        file,
        '/',
        this.scopeType,
        this.scopeId,
        (progress) => {
          this.uploadProgress = progress;
          this.cdr.markForCheck();
        }
      );

      const response =
        this.entityKind === 'user'
          ? await firstValueFrom(
              this.profileImageService.setUserProfileImage(
                this.entityUuid,
                uploadedName
              )
            )
          : await firstValueFrom(
              this.profileImageService.setLocationProfileImage(
                this.entityUuid,
                uploadedName
              )
            );

      this.obfuscatedName = response.profile_image_obfuscated_name;
      this.obfuscatedNameChange.emit(uploadedName);
      await this.loadImage();
    } catch (err) {
      console.error('[DunaProfileImage] Upload failed:', err);
      const msg = 'Kunne ikke uploade billedet. Prøv igen.';
      this.errorMessage = msg;
      this.uploadError.emit(msg);
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
      this.cdr.markForCheck();
    }
  }

  private async loadImage(): Promise<void> {
    const token = ++this.loadToken;
    this.revokeBlobUrl();
    this.imageSrc = null;

    if (!this.obfuscatedName) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const blob = await this.mediaService.downloadFile(this.obfuscatedName);
      if (token !== this.loadToken) {
        return;
      }
      this.blobUrl = URL.createObjectURL(blob);
      this.imageSrc = this.blobUrl;
    } catch (err) {
      console.error('[DunaProfileImage] Failed to load image:', err);
      if (token === this.loadToken) {
        this.imageSrc = null;
      }
    } finally {
      if (token === this.loadToken) {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    }
  }

  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }
}
